/**
 * Firebase App Check bootstrap (A4 monitor-only).
 * Singleton — safe to call from firebase.ts and firebaseWithFailover.ts (same default app).
 */
import type { FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import {
  getFirebaseAppCheckDebugToken,
  getFirebaseAppCheckRecaptchaSiteKey,
  isFirebaseAppCheckConfigured,
} from '../constants/appCheck';
import Logger from '../services/logger';

let appCheckInitialized = false;

export function initFirebaseAppCheck(app: FirebaseApp): void {
  if (appCheckInitialized || typeof window === 'undefined') {
    return;
  }

  if (!isFirebaseAppCheckConfigured()) {
    if (process.env.NODE_ENV === 'development') {
      Logger.info('[AppCheck] Skipped — REACT_APP_FIREBASE_APP_CHECK_RECAPTCHA_SITE_KEY not set');
    }
    return;
  }

  const siteKey = getFirebaseAppCheckRecaptchaSiteKey();
  const debugToken = getFirebaseAppCheckDebugToken();

  if (debugToken && process.env.NODE_ENV === 'development') {
    (window as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugToken;
  }

  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    appCheckInitialized = true;
    Logger.info('[AppCheck] Initialized (Phase 2 enforce — set ENFORCED in Firebase Console for Firestore/Storage/Auth)');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes('already initialized')) {
      appCheckInitialized = true;
      return;
    }
    Logger.warn('[AppCheck] Initialization failed', error instanceof Error ? error : new Error(message));
  }
}

export function isFirebaseAppCheckInitialized(): boolean {
  return appCheckInitialized;
}
